<script lang="ts">
  /**
   * One of the shell's glyphs (`icons.ts` has the set and why it is shaped
   * the way it is). Decoration by default (`aria-hidden`); with `title` it
   * is an image of that name.
   *
   * The filled glyphs (question, info, shield) draw their inner mark in the
   * colour of what they sit on: `--ui-icon-cut`, which a container that is
   * not the ground sets to its own fill (`RowBadge`, an option row's tile);
   * the ground by default.
   *
   * Test id: `testid`. No default. `data-icon` carries the name.
   */
  import { iconStroke, type IconName } from '../icons.js';

  interface Props {
    readonly name: IconName;
    /** Width and height in px. Default 20. */
    readonly size?: number;
    /** Any CSS colour. Default `currentColor`, so the glyph takes its container's colour. */
    readonly color?: string;
    /**
     * Absent (the usual case): decoration, `aria-hidden`, and whatever shows
     * it says what it means in words. Present: the glyph is an image named
     * this (`role="img"`).
     */
    readonly title?: string | undefined;
    readonly testid?: string | undefined;
  }
  const { name, size = 20, color = 'currentColor', title, testid }: Props = $props();

  const stroke = $derived(iconStroke(name));
  const cut = 'var(--ui-icon-cut, var(--ui-ground))';
</script>

<svg
  viewBox="0 0 24 24"
  width={size}
  height={size}
  aria-hidden={title === undefined ? 'true' : undefined}
  role={title === undefined ? undefined : 'img'}
  aria-label={title}
  focusable="false"
  data-testid={testid}
  data-icon={name}
>
  {#if stroke !== undefined}
    <path
      d={stroke}
      fill="none"
      stroke={color}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'play'}
    <path
      d="M8 5v14l11-7L8 5Z"
      fill={color}
      stroke={color}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'trophy'}
    <path d="M7 4h10v3a5 5 0 0 1-5 5 5 5 0 0 1-5-5V4Z" fill={color} />
    <path
      d="M7 5H4v1a4 4 0 0 0 4 4M17 5h3v1a4 4 0 0 1-4 4"
      fill="none"
      stroke={color}
      stroke-width="1.6"
      stroke-linecap="round"
    />
    <rect x="10.5" y="12" width="3" height="3" fill={color} />
    <rect x="8" y="18" width="8" height="2" rx="1" fill={color} />
    <path d="M9.5 15h5l1 3h-7l1-3Z" fill={color} />
  {:else if name === 'calendar'}
    <rect x="4" y="5" width="16" height="15" rx="2" fill="none" stroke={color} stroke-width="2" />
    <path d="M4 9.5h16" stroke={color} stroke-width="2" />
    <path d="M8 3v3.5M16 3v3.5" stroke={color} stroke-width="2" stroke-linecap="round" />
  {:else if name === 'starFilled' || name === 'starOutline'}
    <path
      d="M12 3.5l2.47 5.13 5.53.62-4.1 3.9 1.08 5.55L12 15.9l-4.98 2.8 1.08-5.55-4.1-3.9 5.53-.62L12 3.5Z"
      fill={name === 'starFilled' ? color : 'none'}
      stroke={name === 'starFilled' ? undefined : color}
      stroke-width={name === 'starFilled' ? undefined : '1.5'}
      stroke-linejoin={name === 'starFilled' ? undefined : 'round'}
    />
  {:else if name === 'chevronLeft' || name === 'chevronRight'}
    <path
      d={name === 'chevronLeft' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'}
      fill="none"
      stroke={color}
      stroke-width="2.5"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'gear'}
    <path
      fill={color}
      d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94L14.4 2.81c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41L9.25 5.35c-.59.24-1.13.57-1.62.94L5.24 5.33c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.03-1.58ZM12 15.6a3.6 3.6 0 1 1 0-7.2 3.6 3.6 0 0 1 0 7.2Z"
    />
  {:else if name === 'clock'}
    <circle cx="12" cy="12" r="9" fill="none" stroke={color} stroke-width="2" />
    <path
      d="M12 7v5l3.3 2"
      fill="none"
      stroke={color}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'image'}
    <rect x="3" y="5" width="18" height="14" rx="2" fill="none" stroke={color} stroke-width="2" />
    <circle cx="8.5" cy="10" r="1.5" fill={color} />
    <path
      d="M4.5 16.5 9 12l3.5 3.5 2.5-2.5 4.5 4"
      fill="none"
      stroke={color}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'lock'}
    <rect x="5" y="10" width="14" height="10" rx="2" fill={color} />
    <path
      d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10"
      fill="none"
      stroke={color}
      stroke-width="2.2"
      stroke-linecap="round"
    />
  {:else if name === 'check'}
    <path
      d="M5 12.5 10 17.5 19 7.5"
      fill="none"
      stroke={color}
      stroke-width="3"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'retry'}
    <path
      d="M4 12a8 8 0 0 1 14-5.3M20 12a8 8 0 0 1-14 5.3"
      fill="none"
      stroke={color}
      stroke-width="2.4"
      stroke-linecap="round"
    />
    <path
      d="M18 4v4h-4M6 20v-4h4"
      fill="none"
      stroke={color}
      stroke-width="2.4"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'together'}
    <circle cx="9" cy="8" r="3.2" fill={color} />
    <path d="M3 19a6 6 0 0 1 12 0Z" fill={color} />
    <circle cx="17" cy="9" r="2.4" fill="none" stroke={color} stroke-width="1.8" />
    <path
      d="M14.5 19a4.5 4.5 0 0 1 7-3.7"
      fill="none"
      stroke={color}
      stroke-width="1.8"
      stroke-linecap="round"
    />
  {:else if name === 'more'}
    <circle cx="5" cy="12" r="1.9" fill={color} />
    <circle cx="12" cy="12" r="1.9" fill={color} />
    <circle cx="19" cy="12" r="1.9" fill={color} />
  {:else if name === 'speaker'}
    <path d="M4 9v6h4l5 4V5L8 9H4Z" fill={color} />
    <path
      d="M16.5 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11"
      fill="none"
      stroke={color}
      stroke-width="1.8"
      stroke-linecap="round"
    />
  {:else if name === 'vibrate'}
    <rect
      x="8"
      y="4"
      width="8"
      height="16"
      rx="1.5"
      fill="none"
      stroke={color}
      stroke-width="1.8"
    />
    <path
      d="M4 9v6M2 10.5v3M20 9v6M22 10.5v3"
      fill="none"
      stroke={color}
      stroke-width="1.8"
      stroke-linecap="round"
    />
  {:else if name === 'moon'}
    <path d="M18 13.5A6.5 6.5 0 0 1 10.5 6a6.5 6.5 0 1 0 7.5 7.5Z" fill={color} />
    <path
      d="M15.6 5.2h2.6l-2.6 3.4h2.6"
      fill="none"
      stroke={color}
      stroke-width="1.2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'globe'}
    <circle cx="12" cy="12" r="8" fill="none" stroke={color} stroke-width="1.8" />
    <path
      d="M4 12h16M12 4a12 12 0 0 1 0 16M12 4a12 12 0 0 0 0 16"
      fill="none"
      stroke={color}
      stroke-width="1.8"
    />
  {:else if name === 'account'}
    <circle cx="12" cy="8.5" r="3.5" fill={color} />
    <path
      d="M5 20a7 7 0 0 1 14 0"
      fill="none"
      stroke={color}
      stroke-width="1.8"
      stroke-linecap="round"
    />
  {:else if name === 'shield'}
    <path d="M12 3 5 6v6c0 4 3 7.3 7 9 4-1.7 7-5 7-9V6l-7-3Z" fill={color} />
    <path
      d="m8.8 12.2 2.2 2.2 4.2-4.4"
      fill="none"
      stroke={cut}
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
    />
  {:else if name === 'cap'}
    <path d="M12 4 2 9l10 5 10-5-10-5Z" fill={color} />
    <path
      d="M6 11.5V17c0 1.4 2.7 3 6 3s6-1.6 6-3v-5.5"
      fill="none"
      stroke={color}
      stroke-width="1.8"
    />
  {:else if name === 'question'}
    <circle cx="12" cy="12" r="9" fill={color} />
    <path
      d="M9.6 9.6a2.5 2.5 0 1 1 3.7 2.2c-.8.45-1.3 1-1.3 1.9"
      fill="none"
      stroke={cut}
      stroke-width="2.2"
      stroke-linecap="round"
    />
    <circle cx="12" cy="16.9" r="1.25" fill={cut} />
  {:else if name === 'info'}
    <circle cx="12" cy="12" r="9" fill={color} />
    <circle cx="12" cy="8" r="1.3" fill={cut} />
    <rect x="10.7" y="10.5" width="2.6" height="7" rx="1" fill={cut} />
  {/if}
</svg>
